/**
 * MB-AI-SUPPORT-2C — Fonctions pures partagées par SupportAssistantWidget.
 *
 * Isolées du composant React pour rester testables sans dépendance à
 * @testing-library/react (absent du repo — voir AUDIT_MB-AI-SUPPORT-2.md,
 * partie MB-AI-SUPPORT-2C, §M/§Q pour le détail de cette limite de test).
 */
import { SUPPORT_EMAIL, CONTACT_EMAIL } from '../../config/contactChannels';

export const MESSAGE_MAX_CHARS = 500;

/** Message non vide (après trim) et dans la limite de caractères. */
export function isMessageValid(message) {
  if (typeof message !== 'string') return false;
  return message.trim().length > 0 && message.length <= MESSAGE_MAX_CHARS;
}

/**
 * Construit EXACTEMENT le payload autorisé par le backend (section 6 du
 * ticket) : { message, surface, pathname, language }, rien d'autre — aucun
 * shop_id/user_id/rôle/contenu de page/token/document.
 */
export function buildAskPayload({ message, surface, pathname, language }) {
  return {
    message: (message || '').trim(),
    surface,
    pathname,
    language,
  };
}

const UNAVAILABLE_REASONS = new Set([
  'disabled',
  'no_provider_key',
  'provider_timeout',
  'provider_rate_limit',
  'provider_generic_error',
  'provider_invalid_response',
]);

const NO_ANSWER_REASONS = new Set(['no_documents_in_scope', 'low_relevance']);

/**
 * Traduit la réponse backend `{status, reason}` en état UI.
 * 'answer'      -> réponse affichée normalement
 * 'quota'       -> limite quotidienne atteinte (section 12 : "quota atteint")
 * 'unavailable' -> assistant désactivé / clé absente / erreur fournisseur (section 12 : "assistant désactivé" + fallback générique)
 * 'no_answer'   -> scope résolu mais rien de pertinent trouvé pour cette question précise
 */
export function classifyAssistantResponse(status, reason) {
  if (status === 'ok') return 'answer';
  if (reason === 'rate_limited') return 'quota';
  if (UNAVAILABLE_REASONS.has(reason)) return 'unavailable';
  if (NO_ANSWER_REASONS.has(reason)) return 'no_answer';
  return 'unavailable';
}

/** Email de repli : celui renvoyé par le backend en priorité, sinon celui de la surface. */
export function resolveFallbackEmail(surface, contactEmailFromServer) {
  if (contactEmailFromServer) return contactEmailFromServer;
  return surface === 'marketing' ? CONTACT_EMAIL : SUPPORT_EMAIL;
}

/**
 * Section 19 du ticket : "ne jamais envoyer le texte de la question dans
 * analytics". Ces deux builders sont le SEUL point qui construit les params
 * envoyés à `trackEvent` pour le widget — en ne listant explicitement que
 * les champs autorisés, aucun champ additionnel (message, réponse, contenu
 * de page) ne peut s'y glisser par accident, même si l'appelant en passe un.
 */
export function buildOpenAnalyticsParams({ surface, pathname, language }) {
  return { surface, pathname, language };
}

export function buildQuestionAnalyticsParams({ surface, pathname, language }) {
  return { surface, pathname, language };
}

export function buildFallbackAnalyticsParams({ surface, pathname, language, status }) {
  return { surface, pathname, language, status };
}

/**
 * MB-AI-SUPPORT-3 — événement émis quand un CTA (ex-mailto) déclenche
 * l'ouverture de l'assistant. Mêmes garde-fous que les builders ci-dessus :
 * seuls surface/source/pathname autorisés, jamais de texte de question.
 */
export function buildCtaOpenAnalyticsParams({ surface, source, pathname }) {
  return { surface, source, pathname };
}

/**
 * MB-AI-SUPPORT-HUMAN-FALLBACK-1 — clic sur le lien humain permanent
 * (second niveau, distinct du fallback technique). Mêmes garde-fous :
 * seuls surface/pathname/language autorisés, jamais l'adresse email ni
 * aucune donnée personnelle.
 */
export function buildHumanEmailAnalyticsParams({ surface, pathname, language }) {
  return { surface, pathname, language };
}
